<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import useAutomationValues from 'dashboard/composables/useAutomationValues';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { showActionInput } from 'dashboard/helper/automationHelper';
import {
  buildFlowBranchActionTypes,
  buildFlowBranchFilterTypes,
  defaultFlowBranchAction,
  defaultFlowBranchCondition,
} from './flowBranchAutomation';

const props = defineProps({
  targetIds: {
    type: Array,
    default: () => [],
  },
});
const branches = defineModel('branches', {
  type: Array,
  default: () => [],
});
const defaultNext = defineModel('defaultNext', {
  type: String,
  default: '',
});

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { operators } = useOperators();
const { getConditionDropdownValues, getActionDropdownValues } =
  useAutomationValues();

const matchOptions = computed(() => [
  { value: 'equals', label: t('FLOWS.MATCH_EQUALS') },
  { value: 'contains', label: t('FLOWS.MATCH_CONTAINS') },
]);

const filterTypes = computed(() =>
  buildFlowBranchFilterTypes({
    t,
    operators,
    getConditionDropdownValues,
  })
);

const flowActionTypes = computed(() =>
  buildFlowBranchActionTypes(t, {
    slaEnabled: isCloudFeatureEnabled('sla'),
  })
);

const isAdvanced = branch => {
  const c = branch?.conditions;
  return Array.isArray(c) && c.length > 0;
};

const emitBranches = next => {
  branches.value = next;
};

const updateRow = (index, key, value) => {
  const next = [...(branches.value || [])];
  if (!next[index]) return;
  next[index] = { ...next[index], [key]: value };
  emitBranches(next);
};

const setAdvanced = (index, enabled) => {
  const next = [...(branches.value || [])];
  const row = { ...(next[index] || {}) };
  if (enabled) {
    delete row.match_type;
    delete row.value;
    row.conditions =
      Array.isArray(row.conditions) && row.conditions.length
        ? row.conditions
        : [defaultFlowBranchCondition()];
    row.actions =
      Array.isArray(row.actions) && row.actions.length
        ? row.actions
        : [defaultFlowBranchAction()];
  } else {
    delete row.conditions;
    delete row.actions;
    row.match_type = row.match_type || 'equals';
    row.value = row.value ?? '';
  }
  next[index] = row;
  emitBranches(next);
};

const patchBranch = (index, partial) => {
  const next = [...(branches.value || [])];
  if (!next[index]) return;
  next[index] = { ...next[index], ...partial };
  emitBranches(next);
};

const updateConditions = (index, conditions) => {
  patchBranch(index, { conditions });
};

const updateActions = (index, actions) => {
  patchBranch(index, { actions });
};

const appendCondition = index => {
  const row = branches.value[index];
  const conditions = [...(row.conditions || []), defaultFlowBranchCondition()];
  const lastIdx = conditions.length - 2;
  if (lastIdx >= 0 && conditions[lastIdx]) {
    conditions[lastIdx] = {
      ...conditions[lastIdx],
      query_operator: conditions[lastIdx].query_operator || 'and',
    };
  }
  updateConditions(index, conditions);
};

const removeCondition = (index, condIdx) => {
  const row = branches.value[index];
  const conditions = [...(row.conditions || [])];
  if (conditions.length <= 1) {
    useAlert(t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
    return;
  }
  conditions.splice(condIdx, 1);
  updateConditions(index, conditions);
};

const appendAction = index => {
  const row = branches.value[index];
  updateActions(index, [...(row.actions || []), defaultFlowBranchAction()]);
};

const removeAction = (index, actIdx) => {
  const row = branches.value[index];
  const actions = [...(row.actions || [])];
  if (actions.length <= 1) {
    useAlert(t('AUTOMATION.ACTION.DELETE_MESSAGE'));
    return;
  }
  actions.splice(actIdx, 1);
  updateActions(index, actions);
};

const resetAction = (index, actIdx) => {
  const row = branches.value[index];
  const actions = [...(row.actions || [])];
  actions[actIdx] = { ...actions[actIdx], action_params: [] };
  updateActions(index, actions);
};

const addBranch = () => {
  const next = [
    ...(branches.value || []),
    { match_type: 'equals', value: '', next: props.targetIds[0] || 'end' },
  ];
  emitBranches(next);
};

const removeBranch = index => {
  const next = (branches.value || []).filter((_, i) => i !== index);
  emitBranches(next);
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-for="(row, index) in branches"
      :key="index"
      class="flex flex-col gap-3 rounded-lg bg-n-alpha-2 p-3"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-label-small text-n-slate-11">
          {{ $t('FLOWS.BRANCH_RULE') }} {{ index + 1 }}
        </span>
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-11">{{
            $t('FLOWS.BRANCH_MODE_ADVANCED')
          }}</span>
          <Switch
            :model-value="isAdvanced(row)"
            @update:model-value="setAdvanced(index, $event)"
          />
        </div>
      </div>

      <!-- Legacy equals/contains -->
      <div v-if="!isAdvanced(row)" class="flex flex-wrap items-end gap-2">
        <div class="min-w-[8rem] flex-1">
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            :for="`match-${index}`"
          >
            {{ $t('FLOWS.BRANCH_MATCH') }}
          </label>
          <select
            :id="`match-${index}`"
            :value="row.match_type"
            class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="updateRow(index, 'match_type', $event.target.value)"
          >
            <option
              v-for="opt in matchOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="min-w-[6rem] flex-[1.2]">
          <Input
            :model-value="row.value"
            :label="$t('FLOWS.BRANCH_VALUE')"
            size="sm"
            @update:model-value="updateRow(index, 'value', $event)"
          />
        </div>
        <div class="min-w-[8rem] flex-1">
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            :for="`branch-next-${index}`"
          >
            {{ $t('FLOWS.BRANCH_GOTO') }}
          </label>
          <select
            :id="`branch-next-${index}`"
            :value="row.next"
            class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="updateRow(index, 'next', $event.target.value)"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
        <Button
          faded
          ruby
          size="sm"
          icon="i-lucide-trash-2"
          :aria-label="$t('FLOWS.REMOVE_BRANCH')"
          @click="removeBranch(index)"
        />
      </div>

      <!-- Advanced: conditions + actions -->
      <div v-else class="flex flex-col gap-4">
        <div>
          <p class="mb-2 text-label-small text-n-slate-11">
            {{ $t('FLOWS.BRANCH_CONDITIONS_SECTION') }}
          </p>
          <ul
            class="grid gap-3 list-none p-3 mb-2 outline outline-1 rounded-xl -outline-offset-1 outline-n-weak dark:outline-n-strong"
          >
            <template v-for="(condition, ci) in row.conditions || []" :key="ci">
              <ConditionRow
                v-if="ci === 0"
                v-model:attribute-key="row.conditions[ci].attribute_key"
                v-model:filter-operator="row.conditions[ci].filter_operator"
                v-model:values="row.conditions[ci].values"
                :filter-types="filterTypes"
                :show-query-operator="false"
                @remove="removeCondition(index, ci)"
              />
              <ConditionRow
                v-else
                v-model:attribute-key="row.conditions[ci].attribute_key"
                v-model:filter-operator="row.conditions[ci].filter_operator"
                v-model:query-operator="row.conditions[ci - 1].query_operator"
                v-model:values="row.conditions[ci].values"
                :filter-types="filterTypes"
                show-query-operator
                @remove="removeCondition(index, ci)"
              />
            </template>
            <Button
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('FLOWS.BRANCH_ADD_CONDITION')"
              @click="appendCondition(index)"
            />
          </ul>
        </div>

        <div>
          <p class="mb-2 text-label-small text-n-slate-11">
            {{ $t('FLOWS.BRANCH_ACTIONS_SECTION') }}
          </p>
          <ul
            class="grid list-none gap-3 p-3 mb-2 outline outline-1 rounded-xl -outline-offset-1 outline-n-weak dark:outline-n-strong"
          >
            <AutomationActionInput
              v-for="(action, ai) in row.actions || []"
              :key="ai"
              v-model="row.actions[ai]"
              :action-types="flowActionTypes"
              dropdown-max-height="max-h-[7.5rem]"
              :dropdown-values="getActionDropdownValues(action.action_name)"
              :show-action-input="
                showActionInput(flowActionTypes, action.action_name)
              "
              error-message=""
              @reset-action="resetAction(index, ai)"
              @remove-action="removeAction(index, ai)"
            />
            <Button
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('FLOWS.BRANCH_ADD_ACTION')"
              @click="appendAction(index)"
            />
          </ul>
        </div>

        <div class="min-w-[8rem] max-w-md">
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            :for="`branch-next-v2-${index}`"
          >
            {{ $t('FLOWS.BRANCH_GOTO') }}
          </label>
          <select
            :id="`branch-next-v2-${index}`"
            :value="row.next"
            class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
            @change="updateRow(index, 'next', $event.target.value)"
          >
            <option v-for="tid in targetIds" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>

        <Button
          faded
          ruby
          size="sm"
          class="self-start"
          icon="i-lucide-trash-2"
          :label="$t('FLOWS.REMOVE_BRANCH')"
          @click="removeBranch(index)"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-end gap-2">
      <Button
        faded
        size="sm"
        icon="i-lucide-plus"
        :label="$t('FLOWS.ADD_BRANCH')"
        @click="addBranch"
      />
      <div class="min-w-[10rem] flex-1">
        <label
          class="mb-1 block text-label-small text-n-slate-11"
          for="flow-default-next"
        >
          {{ $t('FLOWS.DEFAULT_NEXT_LABEL') }}
        </label>
        <select
          id="flow-default-next"
          :value="defaultNext"
          class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          @change="defaultNext = $event.target.value"
        >
          <option v-for="tid in targetIds" :key="`d-${tid}`" :value="tid">
            {{ tid }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>
